// Runtime conjugation for the Translate tab. data/verbs.json is the source of
// truth for the ~54 verbs it covers; the dictionary has ~450 verb headwords, so
// every other one needs its table built on the fly:
//
//   • rule-based regular conjugation for the -ar / -er / -ir majority, with the
//     usual spelling shifts (ficar → fique, jogar → joguei, vencer → venço,
//     proteger → protejo, seguir-style gu → g);
//   • a small hand-written table for the genuinely irregular verbs that appear
//     in the dictionary (trazer, pôr, ler, sair, ouvir, perder, …) and the -ir
//     vowel-raising verbs (subir → sobe, mentir → minto, sugerir → sugiro) —
//     for those, only the cells that break the rule are written out; the regular
//     preterite / imperfect / future / conditional rows come from the same
//     generator.
//
// Only the eight columns the Translate card shows are produced.

import verbData from '../../data/verbs.json'
import { endingTypeOf, PRONOUN_KEYS, REGULAR_ENDINGS, type EndingType, type PronounKey } from './conjugation'

export const CONJUGATION_COLUMNS = [
  'present',
  'preterite',
  'imperfect',
  'presentSubjunctive',
  'future',
  'conditional',
  'imperativeAffirmative',
  'imperativeNegative',
] as const
export type ConjugationColumn = (typeof CONJUGATION_COLUMNS)[number]

// Same shape as one verb's `conjugations` block in verbs.json, but only ever
// carrying the columns above.
export type ConjugationGrid = Partial<Record<string, Partial<Record<PronounKey, string>>>>

type VerbEntry = { infinitive: string; conjugations: ConjugationGrid }
const CORE_VERBS = new Map<string, ConjugationGrid>(
  (verbData.verbs as VerbEntry[]).map((verb) => [verb.infinitive, verb.conjugations]),
)

// -ar verbs: preserve the hard stem sound before a front-vowel ending (e / i).
function hardBeforeFront(stem: string): string {
  if (stem.endsWith('c')) return `${stem.slice(0, -1)}qu`
  if (stem.endsWith('ç')) return `${stem.slice(0, -1)}c`
  if (stem.endsWith('g')) return `${stem}u`
  return stem
}

// -er / -ir verbs: preserve the soft stem sound before a back-vowel ending
// (a / o) — vencer → venço, proteger → protejo, seguir-type gu → g.
function softBeforeBack(stem: string): string {
  if (stem.endsWith('gu')) return stem.slice(0, -1)
  if (stem.endsWith('qu')) return `${stem.slice(0, -2)}c`
  if (stem.endsWith('c')) return `${stem.slice(0, -1)}ç`
  if (stem.endsWith('g')) return `${stem.slice(0, -1)}j`
  return stem
}

function regularForm(infinitive: string, type: EndingType, column: string, index: number): string | null {
  const ending = REGULAR_ENDINGS[type][column]?.[index]
  if (ending === null || ending === undefined) return null
  let stem = infinitive.slice(0, -2)
  const firstVowel = ending[0]
  if (type === 'ar') {
    if (firstVowel === 'e' || firstVowel === 'i') stem = hardBeforeFront(stem)
  } else if (firstVowel === 'a' || firstVowel === 'o') {
    stem = softBeforeBack(stem)
  }
  return stem + ending
}

function regularGrid(infinitive: string): ConjugationGrid {
  const type = endingTypeOf(infinitive)
  const grid: ConjugationGrid = {}
  for (const column of CONJUGATION_COLUMNS) {
    const row: Partial<Record<PronounKey, string>> = {}
    PRONOUN_KEYS.forEach((pronoun, index) => {
      const form = regularForm(infinitive, type, column, index)
      if (form === null) return
      row[pronoun] = column === 'imperativeNegative' ? `não ${form}` : form
    })
    grid[column] = row
  }
  return grid
}

// [eu, tu, você, nós, eles] per column; null for a form that doesn't exist
// (the eu imperative, the missing persons of a defective verb).
type Row5 = [string | null, string | null, string | null, string | null, string | null]

// A hand-written irregular row spells out the imperatives, and in the present
// subjunctive they are the same forms: você / nós / eles are the affirmative
// imperative, eu is always the same as você, and tu is the negative imperative
// (or você + s). So the subjunctive never has to be typed out a second time.
function subjunctiveFromImperatives(columns: Partial<Record<ConjugationColumn, Row5>>): Row5 | undefined {
  const affirmative = columns.imperativeAffirmative
  if (!affirmative?.[2]) return undefined
  const voce = affirmative[2]
  const tu = columns.imperativeNegative?.[1]?.replace(/^não /, '') ?? `${voce}s`
  return [voce, tu, voce, affirmative[3], affirmative[4]]
}

function gridFrom(columns: Partial<Record<ConjugationColumn, Row5>>): ConjugationGrid {
  if (!columns.presentSubjunctive) {
    const derived = subjunctiveFromImperatives(columns)
    if (derived) columns = { ...columns, presentSubjunctive: derived }
  }
  const grid: ConjugationGrid = {}
  for (const column of CONJUGATION_COLUMNS) {
    const forms = columns[column]
    if (!forms) continue
    const row: Partial<Record<PronounKey, string>> = {}
    PRONOUN_KEYS.forEach((pronoun, index) => {
      const value = forms[index]
      if (!value) return
      row[pronoun] =
        column === 'imperativeNegative' && !value.startsWith('não ') ? `não ${value}` : value
    })
    grid[column] = row
  }
  return grid
}

// A regular -ar/-er/-ir skeleton with the irregular cells written over it, one
// whole column at a time. Use for verbs whose only irregular columns are
// `present` and the imperatives (the -ir vowel-raisers, perco/valho/impeço…).
function irregular(infinitive: string, columns: Partial<Record<ConjugationColumn, Row5>>): ConjugationGrid {
  return { ...regularGrid(infinitive), ...gridFrom(columns) }
}

const IRREGULAR: Record<string, ConjugationGrid> = {
  // trazer, dizer, fazer, ter, vir … live in verbs.json and are served from
  // CORE_VERBS above; this table is only the irregulars verbs.json doesn't have.
  pôr: gridFrom({
    present: ['ponho', 'pões', 'põe', 'pomos', 'põem'],
    preterite: ['pus', 'puseste', 'pôs', 'pusemos', 'puseram'],
    imperfect: ['punha', 'punhas', 'punha', 'púnhamos', 'punham'],
    future: ['porei', 'porás', 'porá', 'poremos', 'porão'],
    conditional: ['poria', 'porias', 'poria', 'poríamos', 'poriam'],
    imperativeAffirmative: [null, 'põe', 'ponha', 'ponhamos', 'ponham'],
    imperativeNegative: [null, 'ponhas', 'ponha', 'ponhamos', 'ponham'],
  }),
  supor: gridFrom({
    present: ['suponho', 'supões', 'supõe', 'supomos', 'supõem'],
    preterite: ['supus', 'supuseste', 'supôs', 'supusemos', 'supuseram'],
    imperfect: ['supunha', 'supunhas', 'supunha', 'supúnhamos', 'supunham'],
    future: ['suporei', 'suporás', 'suporá', 'suporemos', 'suporão'],
    conditional: ['suporia', 'suporias', 'suporia', 'suporíamos', 'suporiam'],
    imperativeAffirmative: [null, 'supõe', 'suponha', 'suponhamos', 'suponham'],
    imperativeNegative: [null, 'suponhas', 'suponha', 'suponhamos', 'suponham'],
  }),
  haver: gridFrom({
    present: ['hei', 'hás', 'há', 'havemos', 'hão'],
    preterite: ['houve', 'houveste', 'houve', 'houvemos', 'houveram'],
    imperfect: ['havia', 'havias', 'havia', 'havíamos', 'haviam'],
    future: ['haverei', 'haverás', 'haverá', 'haveremos', 'haverão'],
    conditional: ['haveria', 'haverias', 'haveria', 'haveríamos', 'haveriam'],
    presentSubjunctive: ['haja', 'hajas', 'haja', 'hajamos', 'hajam'],
  }),
  ler: irregular('ler', {
    present: ['leio', 'lês', 'lê', 'lemos', 'leem'],
    imperativeAffirmative: [null, 'lê', 'leia', 'leiamos', 'leiam'],
    imperativeNegative: [null, 'leias', 'leia', 'leiamos', 'leiam'],
  }),
  rir: irregular('rir', {
    present: ['rio', 'ris', 'ri', 'rimos', 'riem'],
    imperativeAffirmative: [null, 'ri', 'ria', 'riamos', 'riam'],
    imperativeNegative: [null, 'rias', 'ria', 'riamos', 'riam'],
  }),
  sorrir: irregular('sorrir', {
    present: ['sorrio', 'sorris', 'sorri', 'sorrimos', 'sorriem'],
    imperativeAffirmative: [null, 'sorri', 'sorria', 'sorriamos', 'sorriam'],
    imperativeNegative: [null, 'sorrias', 'sorria', 'sorriamos', 'sorriam'],
  }),
  sair: gridFrom({
    present: ['saio', 'sais', 'sai', 'saímos', 'saem'],
    preterite: ['saí', 'saíste', 'saiu', 'saímos', 'saíram'],
    imperfect: ['saía', 'saías', 'saía', 'saíamos', 'saíam'],
    future: ['sairei', 'sairás', 'sairá', 'sairemos', 'sairão'],
    conditional: ['sairia', 'sairias', 'sairia', 'sairíamos', 'sairiam'],
    imperativeAffirmative: [null, 'sai', 'saia', 'saiamos', 'saiam'],
    imperativeNegative: [null, 'saias', 'saia', 'saiamos', 'saiam'],
  }),
  cair: gridFrom({
    present: ['caio', 'cais', 'cai', 'caímos', 'caem'],
    preterite: ['caí', 'caíste', 'caiu', 'caímos', 'caíram'],
    imperfect: ['caía', 'caías', 'caía', 'caíamos', 'caíam'],
    future: ['cairei', 'cairás', 'cairá', 'cairemos', 'cairão'],
    conditional: ['cairia', 'cairias', 'cairia', 'cairíamos', 'cairiam'],
    imperativeAffirmative: [null, 'cai', 'caia', 'caiamos', 'caiam'],
    imperativeNegative: [null, 'caias', 'caia', 'caiamos', 'caiam'],
  }),
  construir: gridFrom({
    present: ['construo', 'constróis', 'constrói', 'construímos', 'constroem'],
    preterite: ['construí', 'construíste', 'construiu', 'construímos', 'construíram'],
    imperfect: ['construía', 'construías', 'construía', 'construíamos', 'construíam'],
    future: ['construirei', 'construirás', 'construirá', 'construiremos', 'construirão'],
    conditional: ['construiria', 'construirias', 'construiria', 'construiríamos', 'construiriam'],
    imperativeAffirmative: [null, 'constrói', 'construa', 'construamos', 'construam'],
    imperativeNegative: [null, 'construas', 'construa', 'construamos', 'construam'],
  }),
  concluir: gridFrom({
    present: ['concluo', 'concluis', 'conclui', 'concluímos', 'concluem'],
    preterite: ['concluí', 'concluíste', 'concluiu', 'concluímos', 'concluíram'],
    imperfect: ['concluía', 'concluías', 'concluía', 'concluíamos', 'concluíam'],
    future: ['concluirei', 'concluirás', 'concluirá', 'concluiremos', 'concluirão'],
    conditional: ['concluiria', 'concluirias', 'concluiria', 'concluiríamos', 'concluiriam'],
    imperativeAffirmative: [null, 'conclui', 'conclua', 'concluamos', 'concluam'],
    imperativeNegative: [null, 'concluas', 'conclua', 'concluamos', 'concluam'],
  }),
  contribuir: gridFrom({
    present: ['contribuo', 'contribuis', 'contribui', 'contribuímos', 'contribuem'],
    preterite: ['contribuí', 'contribuíste', 'contribuiu', 'contribuímos', 'contribuíram'],
    imperfect: ['contribuía', 'contribuías', 'contribuía', 'contribuíamos', 'contribuíam'],
    future: ['contribuirei', 'contribuirás', 'contribuirá', 'contribuiremos', 'contribuirão'],
    conditional: ['contribuiria', 'contribuirias', 'contribuiria', 'contribuiríamos', 'contribuiriam'],
    imperativeAffirmative: [null, 'contribui', 'contribua', 'contribuamos', 'contribuam'],
    imperativeNegative: [null, 'contribuas', 'contribua', 'contribuamos', 'contribuam'],
  }),
  diminuir: gridFrom({
    present: ['diminuo', 'diminuis', 'diminui', 'diminuímos', 'diminuem'],
    preterite: ['diminuí', 'diminuíste', 'diminuiu', 'diminuímos', 'diminuíram'],
    imperfect: ['diminuía', 'diminuías', 'diminuía', 'diminuíamos', 'diminuíam'],
    future: ['diminuirei', 'diminuirás', 'diminuirá', 'diminuiremos', 'diminuirão'],
    conditional: ['diminuiria', 'diminuirias', 'diminuiria', 'diminuiríamos', 'diminuiriam'],
    imperativeAffirmative: [null, 'diminui', 'diminua', 'diminuamos', 'diminuam'],
    imperativeNegative: [null, 'diminuas', 'diminua', 'diminuamos', 'diminuam'],
  }),
  // -ir / -er verbs irregular only in the present + imperative.
  crer: irregular('crer', {
    present: ['creio', 'crês', 'crê', 'cremos', 'creem'],
    imperativeAffirmative: [null, 'crê', 'creia', 'creiamos', 'creiam'],
    imperativeNegative: [null, 'creias', 'creia', 'creiamos', 'creiam'],
  }),
  ouvir: irregular('ouvir', {
    present: ['ouço', 'ouves', 'ouve', 'ouvimos', 'ouvem'],
    imperativeAffirmative: [null, 'ouve', 'ouça', 'ouçamos', 'ouçam'],
    imperativeNegative: [null, 'ouças', 'ouça', 'ouçamos', 'ouçam'],
  }),
  perder: irregular('perder', {
    present: ['perco', 'perdes', 'perde', 'perdemos', 'perdem'],
    imperativeAffirmative: [null, 'perde', 'perca', 'percamos', 'percam'],
    imperativeNegative: [null, 'percas', 'perca', 'percamos', 'percam'],
  }),
  valer: irregular('valer', {
    present: ['valho', 'vales', 'vale', 'valemos', 'valem'],
    imperativeAffirmative: [null, 'vale', 'valha', 'valhamos', 'valham'],
    imperativeNegative: [null, 'valhas', 'valha', 'valhamos', 'valham'],
  }),
  // e→i like preferir / repetir, but not in verbs.json: referir(-se), "vocês se referem".
  referir: irregular('referir', {
    present: ['refiro', 'referes', 'refere', 'referimos', 'referem'],
    imperativeAffirmative: [null, 'refere', 'refira', 'refiramos', 'refiram'],
    imperativeNegative: [null, 'refiras', 'refira', 'refiramos', 'refiram'],
  }),
  divertir: irregular('divertir', {
    present: ['divirto', 'divertes', 'diverte', 'divertimos', 'divertem'],
    imperativeAffirmative: [null, 'diverte', 'divirta', 'divirtamos', 'divirtam'],
    imperativeNegative: [null, 'divirtas', 'divirta', 'divirtamos', 'divirtam'],
  }),
  impedir: irregular('impedir', {
    present: ['impeço', 'impedes', 'impede', 'impedimos', 'impedem'],
    imperativeAffirmative: [null, 'impede', 'impeça', 'impeçamos', 'impeçam'],
    imperativeNegative: [null, 'impeças', 'impeça', 'impeçamos', 'impeçam'],
  }),
  produzir: irregular('produzir', {
    present: ['produzo', 'produzes', 'produz', 'produzimos', 'produzem'],
    imperativeAffirmative: [null, 'produz', 'produza', 'produzamos', 'produzam'],
    imperativeNegative: [null, 'produzas', 'produza', 'produzamos', 'produzam'],
  }),
  traduzir: irregular('traduzir', {
    present: ['traduzo', 'traduzes', 'traduz', 'traduzimos', 'traduzem'],
    imperativeAffirmative: [null, 'traduz', 'traduza', 'traduzamos', 'traduzam'],
    imperativeNegative: [null, 'traduzas', 'traduza', 'traduzamos', 'traduzam'],
  }),
  proibir: irregular('proibir', {
    present: ['proíbo', 'proíbes', 'proíbe', 'proibimos', 'proíbem'],
    imperativeAffirmative: [null, 'proíbe', 'proíba', 'proibamos', 'proíbam'],
    imperativeNegative: [null, 'proíbas', 'proíba', 'proibamos', 'proíbam'],
  }),
  moer: irregular('moer', {
    present: ['moo', 'móis', 'mói', 'moemos', 'moem'],
    preterite: ['moí', 'moeste', 'moeu', 'moemos', 'moeram'],
    imperfect: ['moía', 'moías', 'moía', 'moíamos', 'moíam'],
    imperativeAffirmative: [null, 'mói', 'moa', 'moamos', 'moam'],
  }),
  subir: irregular('subir', {
    present: ['subo', 'sobes', 'sobe', 'subimos', 'sobem'],
    imperativeAffirmative: [null, 'sobe', 'suba', 'subamos', 'subam'],
    imperativeNegative: [null, 'subas', 'suba', 'subamos', 'subam'],
  }),
  fugir: irregular('fugir', {
    present: ['fujo', 'foges', 'foge', 'fugimos', 'fogem'],
    imperativeAffirmative: [null, 'foge', 'fuja', 'fujamos', 'fujam'],
    imperativeNegative: [null, 'fujas', 'fuja', 'fujamos', 'fujam'],
  }),
  sumir: irregular('sumir', {
    present: ['sumo', 'somes', 'some', 'sumimos', 'somem'],
    imperativeAffirmative: [null, 'some', 'suma', 'sumamos', 'sumam'],
    imperativeNegative: [null, 'sumas', 'suma', 'sumamos', 'sumam'],
  }),
  engolir: irregular('engolir', {
    present: ['engulo', 'engoles', 'engole', 'engolimos', 'engolem'],
    imperativeAffirmative: [null, 'engole', 'engula', 'engulamos', 'engulam'],
    imperativeNegative: [null, 'engulas', 'engula', 'engulamos', 'engulam'],
  }),
  tossir: irregular('tossir', {
    present: ['tusso', 'tosses', 'tosse', 'tossimos', 'tossem'],
    imperativeAffirmative: [null, 'tosse', 'tussa', 'tussamos', 'tussam'],
    imperativeNegative: [null, 'tussas', 'tussa', 'tussamos', 'tussam'],
  }),
  sugerir: irregular('sugerir', {
    present: ['sugiro', 'sugeres', 'sugere', 'sugerimos', 'sugerem'],
    imperativeAffirmative: [null, 'sugere', 'sugira', 'sugiramos', 'sugiram'],
    imperativeNegative: [null, 'sugiras', 'sugira', 'sugiramos', 'sugiram'],
  }),
  competir: irregular('competir', {
    present: ['compito', 'competes', 'compete', 'competimos', 'competem'],
    imperativeAffirmative: [null, 'compete', 'compita', 'compitamos', 'compitam'],
    imperativeNegative: [null, 'compitas', 'compita', 'compitamos', 'compitam'],
  }),
  mentir: irregular('mentir', {
    present: ['minto', 'mentes', 'mente', 'mentimos', 'mentem'],
    imperativeAffirmative: [null, 'mente', 'minta', 'mintamos', 'mintam'],
    imperativeNegative: [null, 'mintas', 'minta', 'mintamos', 'mintam'],
  }),
  investir: irregular('investir', {
    present: ['invisto', 'investes', 'investe', 'investimos', 'investem'],
    imperativeAffirmative: [null, 'investe', 'invista', 'invistamos', 'invistam'],
    imperativeNegative: [null, 'invistas', 'invista', 'invistamos', 'invistam'],
  }),
  transferir: irregular('transferir', {
    present: ['transfiro', 'transferes', 'transfere', 'transferimos', 'transferem'],
    imperativeAffirmative: [null, 'transfere', 'transfira', 'transfiramos', 'transfiram'],
    imperativeNegative: [null, 'transfiras', 'transfira', 'transfiramos', 'transfiram'],
  }),
  // -ar verbs that take -ei- / -eia- in the stressed present.
  odiar: irregular('odiar', {
    present: ['odeio', 'odeias', 'odeia', 'odiamos', 'odeiam'],
    imperativeAffirmative: [null, 'odeia', 'odeie', 'odiemos', 'odeiem'],
    imperativeNegative: [null, 'odeies', 'odeie', 'odiemos', 'odeiem'],
  }),
  pentear: irregular('pentear', {
    present: ['penteio', 'penteias', 'penteia', 'penteamos', 'penteiam'],
    imperativeAffirmative: [null, 'penteia', 'penteie', 'penteemos', 'penteiem'],
    imperativeNegative: [null, 'penteies', 'penteie', 'penteemos', 'penteiem'],
  }),
  presentear: irregular('presentear', {
    present: ['presenteio', 'presenteias', 'presenteia', 'presenteamos', 'presenteiam'],
    imperativeAffirmative: [null, 'presenteia', 'presenteie', 'presenteemos', 'presenteiem'],
    imperativeNegative: [null, 'presenteies', 'presenteie', 'presenteemos', 'presenteiem'],
  }),
  // Defective / impersonal — only the 3rd-person cells exist.
  doer: gridFrom({
    present: [null, null, 'dói', null, 'doem'],
    preterite: [null, null, 'doeu', null, 'doeram'],
    imperfect: [null, null, 'doía', null, 'doíam'],
    future: [null, null, 'doerá', null, 'doerão'],
    conditional: [null, null, 'doeria', null, 'doeriam'],
    presentSubjunctive: [null, null, 'doa', null, 'doam'],
  }),
  chover: gridFrom({
    present: [null, null, 'chove', null, null],
    preterite: [null, null, 'choveu', null, null],
    imperfect: [null, null, 'chovia', null, null],
    future: [null, null, 'choverá', null, null],
    conditional: [null, null, 'choveria', null, null],
    presentSubjunctive: [null, null, 'chova', null, null],
  }),
  nevar: gridFrom({
    present: [null, null, 'neva', null, null],
    preterite: [null, null, 'nevou', null, null],
    imperfect: [null, null, 'nevava', null, null],
    future: [null, null, 'nevará', null, null],
    conditional: [null, null, 'nevaria', null, null],
    presentSubjunctive: [null, null, 'neve', null, null],
  }),
}

// The table for `infinitive`: verbs.json when it has it, else a hand-written
// irregular table, else rule-based regular conjugation. null for anything that
// isn't a single-word -ar / -er / -ir infinitive (a conjugated form that snuck
// into the dictionary, a two-word or reflexive entry).
// Verbs whose only past participle is irregular (fazer → feito), and verbs that
// have both a regular and a short one (pagar → pagado / pago).
const IRREGULAR_PARTICIPLE: Record<string, string> = {
  fazer: 'feito', desfazer: 'desfeito', refazer: 'refeito', satisfazer: 'satisfeito', dizer: 'dito',
  ver: 'visto', rever: 'revisto', prever: 'previsto', vir: 'vindo', pôr: 'posto', compor: 'composto',
  expor: 'exposto', impor: 'imposto', propor: 'proposto', supor: 'suposto', repor: 'reposto',
  dispor: 'disposto', abrir: 'aberto', escrever: 'escrito', descrever: 'descrito', cobrir: 'coberto',
  descobrir: 'descoberto',
}
const SHORT_PARTICIPLE: Record<string, string> = {
  ganhar: 'ganho', gastar: 'gasto', pagar: 'pago', aceitar: 'aceito', entregar: 'entregue', prender: 'preso',
}
const VIR_FAMILY = new Set(['vir', 'intervir', 'convir'])

// The gerund and past participle(s) of any single-word verb: falar → falando, falado;
// fazer → fazendo, feito; pagar → pagando, pagado + pago (both participles are in use).
export function gerundAndParticiples(infinitive: string): { gerund: string; participles: string[] } | null {
  const isPor = /p[oô]r$/.test(infinitive)
  // `*`, not `+`: the two-letter ir is a verb too.
  if (!isPor && !/^[a-zà-ÿ]*(ar|er|ir)$/i.test(infinitive)) return null
  let gerund: string
  if (isPor) gerund = `${infinitive.replace(/p[oô]r$/, 'pond')}o`
  else if (VIR_FAMILY.has(infinitive)) gerund = `${infinitive.slice(0, -3)}vindo`
  else {
    const type = endingTypeOf(infinitive)
    gerund = `${infinitive.slice(0, -2)}${type === 'ar' ? 'ando' : type === 'er' ? 'endo' : 'indo'}`
  }
  const participles: string[] = []
  const irregular = IRREGULAR_PARTICIPLE[infinitive]
  if (irregular) participles.push(irregular)
  else if (isPor) participles.push(infinitive.replace(/p[oô]r$/, 'posto'))
  else participles.push(`${infinitive.slice(0, -2)}${endingTypeOf(infinitive) === 'ar' ? 'ado' : 'ido'}`)
  if (SHORT_PARTICIPLE[infinitive]) participles.push(SHORT_PARTICIPLE[infinitive])
  return { gerund, participles }
}

// The forms the Translate card doesn't tabulate but a reader still meets:
//   • the gerund and past participle ("pensando", "começado", "feito") of any verb;
//   • the imperfect and future subjunctive (which is also the personal
//     infinitive: "para as pessoas assarem") of the rule-based verbs, where the
//     stem never changes. Verbs from verbs.json and the hand-written irregulars
//     are skipped for those, since their stems can differ.
export function extraVerbForms(infinitive: string): string[] {
  if (!/^[a-zà-ÿ]+(ar|er|ir)$/i.test(infinitive)) return []
  const type = endingTypeOf(infinitive)
  const stem = infinitive.slice(0, -2)
  const parts = gerundAndParticiples(infinitive)
  const forms: string[] = parts ? [parts.gerund, ...parts.participles] : []
  if (!CORE_VERBS.has(infinitive) && !IRREGULAR[infinitive]) {
    const endings = REGULAR_ENDINGS[type]
    for (const ending of [...(endings.imperfectSubjunctive ?? []), ...(endings.futureSubjunctive ?? [])]) {
      if (ending !== null) forms.push(`${stem}${ending}`)
    }
  }
  return forms
}

export function conjugationGridFor(infinitive: string): ConjugationGrid | null {
  const core = CORE_VERBS.get(infinitive)
  if (core) return core
  const known = IRREGULAR[infinitive]
  if (known) return known
  if (!/^[a-zà-ÿ]+(ar|er|ir)$/i.test(infinitive)) return null
  return regularGrid(infinitive)
}
